import { Section, SectionHeader } from "@/components/ui/Section";
import { motion } from "framer-motion";
import {
  Brain,
  UserMinus,
  EyeOff,
  Layers,
  Repeat,
  ListChecks,
  TrendingUp,
  Network,
} from "lucide-react";

const problems = [
  {
    icon: Brain,
    title: "AI adoption without workflow redesign",
    description: "Teams bolt on tools without re-thinking how work actually flows. Leverage stays low.",
  },
  {
    icon: UserMinus,
    title: "Founder dependency bottlenecks",
    description: "Every meaningful decision routes through the founder. Velocity caps at one person.",
  },
  {
    icon: EyeOff,
    title: "Fragmented operational visibility",
    description: "Dashboards in five places. No single view of where the business actually is.",
  },
  {
    icon: Layers,
    title: "MVPs built without scale thinking",
    description: "Speed early, debt later. Architecture choices that block the next two stages.",
  },
  {
    icon: Repeat,
    title: "Manual research and repetitive operations",
    description: "Senior bandwidth burned on workflows AI and automation should be carrying.",
  },
  {
    icon: ListChecks,
    title: "Product prioritization chaos",
    description: "Roadmaps shaped by the loudest voice, not by business outcome or systems thinking.",
  },
  {
    icon: TrendingUp,
    title: "Investor readiness and tech clarity",
    description: "Technology narratives that fall apart under serious diligence and architecture review.",
  },
  {
    icon: Network,
    title: "Scaling execution across teams and systems",
    description: "Adding people without adding governance, observability, or operating cadence.",
  },
];

export function WhatWeSolveSection() {
  return (
    <Section background="default">
      <SectionHeader
        eyebrow="What we help solve"
        variant="dark"
        title="The operational and strategic friction that quietly limits founder-led companies."
        subtitle="We work on the problems that aren't on the roadmap, but show up in every leadership conversation."
      />

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        {problems.map((p, i) => (
          <motion.div
            key={p.title}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: (i % 4) * 0.06, ease: [0.21, 0.47, 0.32, 0.98] }}
            viewport={{ once: true, margin: "-60px" }}
            className="group relative rounded-2xl p-6 h-full bg-white/[0.035] border border-white/10 backdrop-blur-md hover:border-accent/35 hover:bg-white/[0.06] transition-all duration-500 hover:-translate-y-1"
          >
            <div className="w-10 h-10 rounded-xl bg-white/[0.06] border border-white/10 grid place-items-center mb-5 group-hover:bg-accent/15 group-hover:border-accent/40 transition-colors">
              <p.icon className="h-4 w-4 text-accent" strokeWidth={1.6} />
            </div>
            <h3 className="text-[15px] font-display font-semibold text-white mb-2 leading-snug">
              {p.title}
            </h3>
            <p className="text-[13.5px] text-white/60 leading-[1.65]">{p.description}</p>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}